import React from 'react';
import {
    BatteryHigh,
    CaretDown,
    Check,
    Drop,
    Eye,
    House,
    Lightning,
    Minus,
    Plus,
    SunHorizon,
} from '@phosphor-icons/react';
import { SOLAR_NON_FINALIZED_WARNING } from '../../../../../../helpers/systemDesignSolarMetrics';
import { useDashboardV3Data, type V3ProductCardModel } from './useDashboardV3Data';

export type DashboardV3ProductCardProps = {
    model: V3ProductCardModel;
    onDesign?: () => void;
    onUtility?: () => void;
    onAdders?: () => void;
    onFinance?: () => void;
    onWastageDecrease?: () => void;
    onWastageIncrease?: () => void;
    onShingleClick?: () => void;
    /** Shown below metrics when finance/product selection is incomplete */
    warningMessage?: string;
};

const ICONS = {
    solar: SunHorizon,
    roof: House,
    battery: BatteryHigh,
    gutter: Drop,
} as const;

export const DashboardV3ProductCard: React.FC<DashboardV3ProductCardProps> = ({
    model,
    onDesign,
    onUtility,
    onAdders,
    onFinance,
    onWastageDecrease,
    onWastageIncrease,
    onShingleClick,
    warningMessage,
}) => {
    const Icon = ICONS[model.kind];
    const showCheck = model.statusTone === 'signed';
    const { solarMetricsOutOfSync } = useDashboardV3Data();
    const showNonFinalizedMetricsWarning = solarMetricsOutOfSync && model.kind === 'solar';

    return (
        <article
            className={`obd-v3-product-card obd-v3-product-card--${model.kind}${showNonFinalizedMetricsWarning ? ' obd-v3-product-card--metrics-out-of-sync' : ''}`}
            data-status={model.statusTone}
        >
            <header className="obd-v3-product-card__head">
                <div className="obd-v3-product-card__head-main">
                    <span className="obd-v3-product-card__icon" aria-hidden>
                        <Icon size={22} weight="duotone" />
                    </span>
                    <div className="obd-v3-product-card__head-text">
                        <h3 className="obd-v3-product-card__title">{model.title}</h3>
                        <p className="obd-v3-product-card__subtitle">{model.subtitle}</p>
                    </div>
                </div>
                <span
                    className={`obd-v3-product-card__status obd-v3-product-card__status--${model.statusTone}`}
                >
                    {showCheck ? <Check size={12} weight="bold" aria-hidden /> : null}
                    {model.statusLabel}
                </span>
            </header>

            <div className="obd-v3-product-card__body">
                <div className="obd-v3-product-card__metrics">
                    {model.metrics.map((m) => (
                        <div
                            key={m.label}
                            className={`obd-v3-product-card__metric${m.emphasis ? ' obd-v3-product-card__metric--emphasis' : ''}`}
                        >
                            <span className="obd-v3-product-card__metric-label">{m.label}</span>
                            <span className="obd-v3-product-card__metric-value">{m.value}</span>
                        </div>
                    ))}
                </div>

                {(warningMessage || showNonFinalizedMetricsWarning) ? (
                    <p className="obd-v3-product-card__warning" role="status">
                        {warningMessage ?? SOLAR_NON_FINALIZED_WARNING}
                    </p>
                ) : null}

                {model.kind === 'roof' && model.showWastage ? (
                    <div className="obd-v3-product-card__roof-controls">
                        <div className="obd-v3-product-card__wastage">
                            <span className="obd-v3-product-card__control-label">Wastage %</span>
                            <div className="obd-v3-product-card__stepper" aria-label="Wastage percent">
                                <button
                                    type="button"
                                    className="obd-v3-product-card__stepper-btn"
                                    aria-label="Decrease wastage"
                                    onClick={onWastageDecrease}
                                >
                                    <Minus size={12} weight="bold" />
                                </button>
                                <span className="obd-v3-product-card__stepper-value">
                                    {model.wastagePercent != null ? `${model.wastagePercent}%` : '—'}
                                </span>
                                <button
                                    type="button"
                                    className="obd-v3-product-card__stepper-btn"
                                    aria-label="Increase wastage"
                                    onClick={onWastageIncrease}
                                >
                                    <Plus size={12} weight="bold" />
                                </button>
                            </div>
                        </div>
                        <div className="obd-v3-product-card__shingle">
                            <span className="obd-v3-product-card__control-label">Shingle</span>
                            <button
                                type="button"
                                className="obd-v3-product-card__shingle-select"
                                aria-label={`Shingle: ${model.shingleLabel ?? '—'}`}
                                onClick={onShingleClick}
                            >
                                <span
                                    className="obd-v3-product-card__shingle-swatch"
                                    style={{ background: model.shingleSwatch ?? '#3d3d3d' }}
                                    aria-hidden
                                />
                                <span className="obd-v3-product-card__shingle-name">
                                    {model.shingleLabel ?? '—'}
                                </span>
                                <CaretDown size={12} weight="bold" aria-hidden />
                            </button>
                        </div>
                    </div>
                ) : null}
            </div>

            <footer className="obd-v3-product-card__foot">
                <button
                    type="button"
                    className="obd-v3-product-card__finance"
                    onClick={onFinance}
                    aria-label={`Finance: ${model.financePartner}${model.financeMethod ? `, ${model.financeMethod}` : ''}`}
                >
                    <span className="obd-v3-product-card__finance-dot" aria-hidden />
                    <span className="obd-v3-product-card__finance-partner">{model.financePartner}</span>
                    {model.financeMethod ? (
                        <>
                            <span className="obd-v3-product-card__finance-method">{model.financeMethod}</span>
                            <CaretDown size={12} weight="bold" className="obd-v3-product-card__finance-caret" aria-hidden />
                        </>
                    ) : null}
                </button>
                <div className="obd-v3-product-card__actions" role="group" aria-label="Product actions">
                    <button type="button" className="obd-v3-product-card__action" onClick={onDesign}>
                        <Eye size={14} weight="regular" aria-hidden />
                        Design
                    </button>
                    {model.kind === 'solar' ? (
                        <button type="button" className="obd-v3-product-card__action" onClick={onUtility}>
                            <Lightning size={14} weight="duotone" aria-hidden />
                            Utility
                        </button>
                    ) : null}
                    <button type="button" className="obd-v3-product-card__action" onClick={onAdders}>
                        <Plus size={14} weight="bold" aria-hidden />
                        Adders
                    </button>
                </div>
            </footer>
        </article>
    );
};
